import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AlertCircle, ArrowLeft, Building2, Info, Save, X } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import SectionCard from "@/components/shared/SectionCard";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import {
  useStudentWorkLog,
  useUpdateStudentWorkLog,
} from "@/hooks/useStudentData";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Field } from "@/components/forms";
import { DatePicker } from "@/components/ui/date-picker";

export const Route = createFileRoute("/student/work-logs/edit/$id")({
  component: EditWorkLog,
});

function EditWorkLog() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: log, isLoading } = useStudentWorkLog(id);
  const updateLog = useUpdateStudentWorkLog();

  const [date, setDate] = useState("");
  const [hours, setHours] = useState("");
  const [description, setDescription] = useState("");
  const [challenge, setChallenge] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [loaded, setLoaded] = useState(false);

  // Fill the form once the log arrives. Guarded so a background refetch does
  // not overwrite edits the student is part-way through making.
  useEffect(() => {
    if (log && !loaded) {
      setDate(log.date ? String(log.date).slice(0, 10) : "");
      setHours(String(log.hours ?? ""));
      setDescription(log.description || log.content || "");
      setChallenge(log.challenges || log.challenge || "");
      setLoaded(true);
    }
  }, [log, loaded]);

  if (isLoading || !loaded) {
    return <PageSkeleton />;
  }

  if (!log) {
    return (
      <EmptyState
        title="Work log not found"
        description="This entry may have been removed or you may not have access to it."
        actionLabel="Back to list"
        onAction={() => navigate({ to: "/student/work-logs" })}
      />
    );
  }

  // The same rule the API enforces, so the screen does not offer an edit that
  // would be refused.
  const isEditable = log.status === "pending" || log.status === "changes_requested";
  if (!isEditable) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Edit work log"
          breadcrumbs={[
            { label: "Work Logs", path: "/student/work-logs" },
            { label: "Detail", path: `/student/work-logs/${id}` },
          ]}
        />
        <Alert variant="info">
          <Info />
          <AlertDescription className="text-foreground">
            This log has already been reviewed and can no longer be edited.
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          onClick={() => navigate({ to: "/student/work-logs/$id", params: { id: String(id) } })}
        >
          <ArrowLeft />
          Back to the log
        </Button>
      </div>
    );
  }

  const validate = () => {
    const next: Record<string, string> = {};
    const hoursValue = Number(hours);
    if (!date) next.date = "Date is required";
    if (!hours || Number.isNaN(hoursValue) || hoursValue <= 0 || hoursValue > 24)
      next.hours = "Enter between 0 and 24 hours";
    if (description.trim().length < 10)
      next.description = "Please describe the work in a little more detail";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError("");
    if (!validate()) return;
    try {
      await updateLog.mutateAsync({
        id: String(id),
        date,
        hours: Number(hours),
        description,
        challenge: challenge || undefined,
      });
      navigate({ to: "/student/work-logs/$id", params: { id: String(id) } });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "The work log could not be updated. Please try again.";
      setSubmitError(message);
    }
  };

  const partnerName = log.partner_name || log.partnerName || "—";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit work log"
        subtitle="Correct the details of a submission that has not been reviewed yet."
        breadcrumbs={[
          { label: "Dashboard", path: "/student/dashboard" },
          { label: "Work Logs", path: "/student/work-logs" },
          { label: "Edit" },
        ]}
      />

      <div className="max-w-3xl">
        <SectionCard title="Task details">
          <div className="space-y-5">
            <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-4 py-3">
              <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <div className="min-w-0">
                <p className="eyebrow">Partner</p>
                <p className="truncate text-sm text-foreground">{partnerName}</p>
              </div>
            </div>

            <Alert variant="info">
              <Info />
              <AlertDescription className="text-foreground">
                The partner this log belongs to cannot be changed. If the hours were for a
                different partner, withdraw this entry and submit a new one.
              </AlertDescription>
            </Alert>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Date of work" error={errors.date}>
                <DatePicker
                  value={date}
                  onChange={(v) => setDate(v)}
                  max={new Date().toISOString().slice(0, 10)}
                />
              </Field>
              <Field label="Hours" error={errors.hours}>
                <Input
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  min={0}
                  max={24}
                  step={0.5}
                />
              </Field>
            </div>

            <Field label="Tasks description" error={errors.description}>
              <Textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>

            <Field label="Challenges faced (optional)">
              <Textarea rows={2} value={challenge} onChange={(e) => setChallenge(e.target.value)} />
            </Field>

            <Separator />

            {submitError && (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription className="flex-1 text-foreground">{submitError}</AlertDescription>
                <button
                  type="button"
                  onClick={() => setSubmitError("")}
                  aria-label="Dismiss"
                  className="-my-0.5 rounded-xs p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </Alert>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSubmit} loading={updateLog.isPending}>
                {!updateLog.isPending && <Save />}
                {updateLog.isPending ? "Saving..." : "Save changes"}
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  navigate({ to: "/student/work-logs/$id", params: { id: String(id) } })
                }
              >
                Cancel
              </Button>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
